import type { ApiResponse } from '../../api';
import { rosterApiError } from '../roster/roster.raw-request';
import { FileCabinetPage, FileCabinetRequestError } from './file-cabinet.types';

/**
 * Items plus the total from `pagination-total`. Every File Cabinet list sends one — `/phone/ai`
 * included, which the design assumed it did not (sandbox, 2026-10-22). A missing total is an
 * error, not zero (NS-3).
 */
export async function fileCabinetPage<T>(raw: ApiResponse<T[]>): Promise<FileCabinetPage<T>> {
    const items = await raw.value();
    const header = raw.raw.headers.get('pagination-total');
    const total = header === null ? NaN : Number(header);
    if (!Number.isFinite(total)) {
        throw new FileCabinetRequestError('The response carried no total.');
    }
    return { items, total };
}

/**
 * Runs a client call. The generated runtime throws the failed `Response` itself; this turns it into
 * a `FileCabinetRequestError` that names the route and keeps the status, so the screen can tell
 * "no access" (403) from "failed" — the roster's error formatting, reused.
 */
export async function fileCabinetCall<T>(call: () => Promise<T>): Promise<T> {
    try {
        return await call();
    } catch (error) {
        if (error instanceof FileCabinetRequestError) {
            throw error;
        }
        // Its message carries the refused URL with its query string — the search box text. Say what
        // happened without repeating what was typed.
        if (error instanceof Error && error.name === 'DemoModeBlockedError') {
            throw new FileCabinetRequestError('Demo mode refused a request outside the demo data, so nothing was loaded.');
        }
        const response = typeof Response !== 'undefined' && error instanceof Response ? error : undefined;
        const described = await rosterApiError(error, 'GET');
        throw new FileCabinetRequestError(described.message, response?.status, response ? await codeOf(response) : undefined);
    }
}

/** The server's `code`, or `invalid_id` when a 400 rejects the `id` field — read from a copy of the body. */
async function codeOf(response: Response): Promise<string | undefined> {
    try {
        const body = await response.clone().json();
        if (typeof body?.code === 'string') {
            return body.code;
        }
        return response.status === 400 && body?.errors?.id ? 'invalid_id' : undefined;
    } catch {
        return undefined;
    }
}
